import { nanoid } from "nanoid";
import { merge } from "smob";
import type { PartialDeep } from "type-fest";
import { createReference } from "../lib/firebase";
import { FirebaseStore } from "../lib/FirebaseStore";

const FIREBASE_KEY = "descendants";

// TYPES
// -----

/** The information of one descendant (real or generated) in the family tree */
export interface DescendantData {
  id: string;

  /**
   * Urls for the images that are available for this person.
   * For a real person, this is the selfie, for a generated person, these are the generated images
   */
  images: string[];

  /**
   * The index of the image that is currently active for this person
   * null = user has not selected an image yet (not approved)
   * number = user has selected this image index (approved)
   */
  chosenImageIndex: number | null;

  /**
   * The ids of the 2-3 descendants that are parents of this person
   */
  ancestorIds?: string[];

  /**
   * The ids of all users that are part of this person's family tree
   */
  userIds: string[];

  /**
   * If we use 'alien/future' families we want to know which Descendant is a repeating group from the previous round
   */
  repeatedUsers?: boolean;
}

export interface DescendantsById {
  [id: string]: DescendantData;
}

/**
 * Check if a descendant has an approved image
 * @param descendant - The descendant to check
 * @returns true if the descendant has images and the user has selected one (chosenImageIndex is not null)
 */
export function hasApprovedImage(descendant: DescendantData): boolean {
  return descendant.chosenImageIndex !== null && (descendant.images?.length ?? 0) > 0;
}

// DESCENDANT
// ----------

interface DescendantOptions {
  eventPrefix: string;
  id: string;
}

export class Descendant extends FirebaseStore<DescendantData> {
  constructor({ eventPrefix, id }: DescendantOptions) {
    super(createReference(eventPrefix, FIREBASE_KEY, id));
  }
}

// ALL DESCENDANTS
// ---------------

interface AllDescendantsOptions {
  eventPrefix: string;
}

export class AllDescendantsStore extends FirebaseStore<DescendantsById> {
  constructor({ eventPrefix }: AllDescendantsOptions) {
    super(createReference(eventPrefix, FIREBASE_KEY));
  }

  async createDescendant(partialDescendant: Omit<PartialDeep<DescendantData>, "id">) {
    const id = nanoid();

    if (!partialDescendant.images?.length) {
      return "images-empty";
    }

    // Make sure the id is always the same as the ref.key
    // Merge default values into the partialDescendant if they are not present
    const descendant: DescendantData = merge({ id }, partialDescendant, {
      images: [],
      chosenImageIndex: null,
      userIds: [],
    });

    await this.update({ [id]: descendant });

    return null;
  }
}
